import { useEffect, useRef } from 'react'

const TOOLS = [
  'JavaScript',
  'jQuery',
  'p5',
  'NodeJS',
  'Expo',
  'Typesense',
  'React Native',
  'React',
  'Babel',
  'GraphQL',
  'Webpack',
  'Java',
  'Android',
  'TensorFlow',
  'PHP',
  'Zend',
  'Symfony',
  'MySQL',
  'MongoDB',
  'GitHub',
]

const COLS = 4
const ROWS = Math.ceil(TOOLS.length / COLS)
const LARGE_SCREEN_MIN = 1024
const TARGET_SCALE = 1.65
const NEIGHBOUR_SCALE = 0.78
const PUSH_AMOUNT_RATIO = 0.14
const LARGE_ITEM_GAP = 20 // extra px between items on big screens

function isZoomDisabled() {
  return window.matchMedia('(pointer: coarse), (max-width: 768px)').matches
}

// Water-surface float parameters per item (seeded deterministically)
function seedParams(i) {
  const s = (i * 2654435761) >>> 0
  const s2 = (s * 2654435761) >>> 0
  const s3 = (s2 * 2654435761) >>> 0
  const s4 = (s3 * 2654435761) >>> 0
  return {
    ax: 3 + (s % 100) / 100 * 3,
    fx: 0.0004 + (s2 % 100) / 100 * 0.0003,
    px: (s3 % 628) / 100,
    ay: 4 + (s4 % 100) / 100 * 4,
    fy: 0.0006 + (s % 100) / 100 * 0.0004,
    py: (s2 % 628) / 100,
  }
}

const PARAMS = TOOLS.map((_, i) => seedParams(i))

export default function Tools({ onImagesLoaded }) {
  const wrapRef = useRef(null)
  const canvasRef = useRef(null)
  const imagesRef = useRef([])
  const hoveredRef = useRef(-1)
  const scaleRef = useRef(TOOLS.map(() => 1))
  const pushRef = useRef(TOOLS.map(() => 0))
  const rafRef = useRef(0)
  const loadedCount = useRef(0)

  const layoutRef = useRef({
    cellW: 140,
    cellH: 130,
    iconSize: 80,
    logW: 560,
    logH: 520,
    labelGap: 24,
    padX: 0,
    padY: 0,
  })

  // Load all images once
  useEffect(() => {
    const imgs = TOOLS.map((name) => {
      const img = new Image()
      img.src = `images/${name.toLowerCase().replace(/ /g, '_')}.png`
      return img
    })
    imagesRef.current = imgs

    imgs.forEach((img) => {
      img.onload = () => {
        loadedCount.current += 1
        if (loadedCount.current === TOOLS.length) onImagesLoaded()
      }
      img.onerror = () => {
        loadedCount.current += 1
        if (loadedCount.current === TOOLS.length) onImagesLoaded()
      }
    })
  }, [onImagesLoaded])

  useEffect(() => {
    const canvas = canvasRef.current
    const wrap = wrapRef.current
    if (!canvas || !wrap) return

    const ctx = canvas.getContext('2d')
    let running = true

    const getContainerWidth = () => {
      const rect = wrap.getBoundingClientRect()
      const w = rect.width || window.innerWidth
      // leave a little room for padding/scrollbars
      return Math.max(320, w)
    }

    const recalcLayout = () => {
      const containerW = getContainerWidth()
      let cellW, cellH, iconSize, padX = 0, padY = 0

      if (containerW >= LARGE_SCREEN_MIN) {
        // Big screens: larger icons with extra breathing room between neighbours
        iconSize = Math.min(148, Math.max(96, containerW * 0.105))
        const gapX = iconSize * 0.12 + LARGE_ITEM_GAP
        const gapY = iconSize * 0.1 + LARGE_ITEM_GAP
        cellW = iconSize + gapX
        cellH = iconSize + gapY + iconSize * 0.12

        // Pad canvas so zoom + neighbour push stay inside bounds
        const scaleOverflow = (iconSize * (TARGET_SCALE - 1)) / 2
        const pushOverflow = cellW * PUSH_AMOUNT_RATIO
        const floatOverflow = 6 * (cellW / 140)
        padX = Math.ceil(scaleOverflow + pushOverflow + floatOverflow)
        padY = Math.ceil(scaleOverflow + floatOverflow)
      } else {
        cellW = containerW / COLS
        cellH = cellW * (130 / 140)
        iconSize = Math.max(56, Math.min(118, cellW * 0.68))
      }

      // label pill sits below the icon
      const labelGap = Math.max(18, cellH * 0.14)
      const labelOverflow = containerW >= LARGE_SCREEN_MIN ? 44 : 0
      const logW = padX * 2 + cellW * COLS
      const logH = padY * 2 + ROWS * cellH + labelGap + 26 + labelOverflow

      layoutRef.current = { cellW, cellH, iconSize, logW, logH, labelGap, padX, padY }

      const dpr = window.devicePixelRatio || 1
      canvas.width = logW * dpr
      canvas.height = logH * dpr
      canvas.style.width = `${logW}px`
      canvas.style.height = `${logH}px`

      // reset transform (ctx is reused across resizes)
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    }

    recalcLayout()
    const ro = new ResizeObserver(() => recalcLayout())
    ro.observe(wrap)
    window.addEventListener('resize', recalcLayout)

    const SPRING = 0.14

    const itemCenter = (idx, layout) => {
      const col = idx % COLS
      const row = Math.floor(idx / COLS)
      const padX = layout.padX || 0
      const padY = layout.padY || 0
      return {
        cx: padX + col * layout.cellW + layout.cellW / 2,
        cy: padY + row * layout.cellH + layout.cellH / 2,
      }
    }

    const hitTest = (lx, ly, t) => {
      const layout = layoutRef.current
      for (let i = TOOLS.length - 1; i >= 0; i--) {
        const p = PARAMS[i]
        const { cx, cy } = itemCenter(i, layout)
        // scale float amplitude with layout so motion "feels" consistent
        const ax = p.ax * (layout.cellW / 140)
        const ay = p.ay * (layout.cellH / 130)
        const ox = Math.sin(t * p.fx + p.px) * ax
        const oy = Math.sin(t * p.fy + p.py) * ay

        const scale = scaleRef.current[i]
        const half = (layout.iconSize * scale) / 2
        const pushX = pushRef.current[i]
        const nx = cx + ox + pushX
        const ny = cy + oy

        if (
          lx >= nx - half &&
          lx <= nx + half &&
          ly >= ny - half &&
          ly <= ny + half
        ) {
          return i
        }
      }
      return -1
    }

    const toLogical = (e) => {
      const rect = canvas.getBoundingClientRect()
      const layout = layoutRef.current
      const scaleX = layout.logW / rect.width
      const scaleY = layout.logH / rect.height
      const clientX = e.touches ? e.touches[0].clientX : e.clientX
      const clientY = e.touches ? e.touches[0].clientY : e.clientY
      return {
        lx: (clientX - rect.left) * scaleX,
        ly: (clientY - rect.top) * scaleY,
      }
    }

    const draw = (t) => {
      if (!running) return

      const layout = layoutRef.current
      const dpr = window.devicePixelRatio || 1

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      ctx.clearRect(0, 0, layout.logW, layout.logH)

      const zoomDisabled = isZoomDisabled()
      const hov = zoomDisabled ? -1 : hoveredRef.current

      const hovRow = hov === -1 ? -1 : Math.floor(hov / COLS)

      // Update scales + push targets toward hover state
      for (let i = 0; i < TOOLS.length; i++) {
        let scaleTarget = 1
        let pushTarget = 0

        if (!zoomDisabled && i === hov) {
          scaleTarget = TARGET_SCALE
        } else if (!zoomDisabled && hov !== -1) {
          const iRow = Math.floor(i / COLS)
          const dist = Math.abs(i - hov)
          const sameRow = iRow === hovRow
          const isNeighbour = sameRow && dist === 1
          if (isNeighbour) {
            scaleTarget = NEIGHBOUR_SCALE
            const sign = i < hov ? -1 : 1
            pushTarget = sign * layout.cellW * PUSH_AMOUNT_RATIO
          }
        }

        scaleRef.current[i] += (scaleTarget - scaleRef.current[i]) * SPRING
        pushRef.current[i] += (pushTarget - pushRef.current[i]) * SPRING
      }

      // Draw back-to-front, hover on top
      const order = [...Array(TOOLS.length).keys()]
      if (hov !== -1) {
        order.splice(order.indexOf(hov), 1)
        order.push(hov)
      }

      for (const i of order) {
        const p = PARAMS[i]
        const { cx, cy } = itemCenter(i, layout)

        const ax = p.ax * (layout.cellW / 140)
        const ay = p.ay * (layout.cellH / 130)
        const ox = Math.sin(t * p.fx + p.px) * ax
        const oy = Math.sin(t * p.fy + p.py) * ay

        const scale = scaleRef.current[i]
        const size = layout.iconSize * scale
        const nx = cx + ox + pushRef.current[i]
        const ny = cy + oy

        ctx.save()
        ctx.translate(nx, ny)

        if (scale > 1.02) {
          ctx.shadowColor = 'rgba(0,0,0,0.25)'
          ctx.shadowBlur = 10 * (scale - 1)
          ctx.shadowOffsetY = 4 * (scale - 1)
        }

        const img = imagesRef.current[i]
        if (img?.complete && img.naturalWidth) {
          ctx.drawImage(img, -size / 2, -size / 2, size, size)
        }

        ctx.restore()

        const labelAlpha = Math.max(0, (scale - 1.08) / (TARGET_SCALE - 1.08))
        if (labelAlpha > 0.01) {
          ctx.save()
          ctx.globalAlpha = labelAlpha
          ctx.font = `bold ${Math.max(10, Math.min(14, layout.cellW * 0.12))}px "Roboto", sans-serif`
          ctx.textAlign = 'center'
          ctx.textBaseline = 'top'

          const text = TOOLS[i]
          const tw = ctx.measureText(text).width
          const px = 8
          const py = 4
          const rad = 6
          const bx = nx - tw / 2 - px
          const by = ny + size / 2 + 6
          const bw = tw + px * 2
          const bh = 14 + py * 2

          ctx.fillStyle = 'rgba(255,255,255,0.88)'
          ctx.beginPath()
          ctx.roundRect(bx, by, bw, bh, rad)
          ctx.fill()

          ctx.fillStyle = '#444'
          ctx.fillText(text, nx, by + py)
          ctx.restore()
        }
      }

      rafRef.current = requestAnimationFrame(draw)
    }

    rafRef.current = requestAnimationFrame(draw)

    const onMove = (e) => {
      if (isZoomDisabled()) return
      const { lx, ly } = toLogical(e)
      hoveredRef.current = hitTest(lx, ly, performance.now())
    }
    const onLeave = () => {
      hoveredRef.current = -1
    }
    const onTouchStart = (e) => {
      if (isZoomDisabled()) return
      e.preventDefault()
      const { lx, ly } = toLogical(e)
      hoveredRef.current = hitTest(lx, ly, performance.now())
    }
    const onTouchEnd = () => {
      if (isZoomDisabled()) return
      hoveredRef.current = -1
    }

    const syncInteractionMode = () => {
      const disabled = isZoomDisabled()
      canvas.style.touchAction = disabled ? 'auto' : 'none'
      canvas.style.cursor = disabled ? 'default' : 'pointer'
      if (disabled) hoveredRef.current = -1
    }

    canvas.addEventListener('mousemove', onMove)
    canvas.addEventListener('mouseleave', onLeave)
    canvas.addEventListener('touchstart', onTouchStart, { passive: false })
    canvas.addEventListener('touchend', onTouchEnd)

    syncInteractionMode()
    const zoomMq = window.matchMedia('(pointer: coarse), (max-width: 768px)')
    const onZoomMqChange = () => syncInteractionMode()
    zoomMq.addEventListener('change', onZoomMqChange)

    return () => {
      running = false
      cancelAnimationFrame(rafRef.current)
      window.removeEventListener('resize', recalcLayout)
      ro.disconnect()
      zoomMq.removeEventListener('change', onZoomMqChange)
      canvas.removeEventListener('mousemove', onMove)
      canvas.removeEventListener('mouseleave', onLeave)
      canvas.removeEventListener('touchstart', onTouchStart)
      canvas.removeEventListener('touchend', onTouchEnd)
    }
  }, [onImagesLoaded])

  return (
    <div
      ref={wrapRef}
      style={{ width: '100%', display: 'flex', justifyContent: 'center', padding: '16px 0 32px' }}
    >
      <canvas
        ref={canvasRef}
        style={{
          display: 'block',
        }}
      />
    </div>
  )
}
